import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import { getEarningsDeductionType, addTransaction } from './action.ts';
import { toast } from 'react-toastify';

interface AddEarningModalProps {
  open: boolean;
  onClose: () => void;
  driverName?: string;
  driverId?: string | number;
  warehouseId?: string | null;
  mode?: 'earning' | 'deduction';
  month:any;
  onSuccess?: () => void; 
}

interface DropdownOption {
  id: string | number;
  label: string;
  category?: string;
}

const AddEarningModal: React.FC<AddEarningModalProps> = ({
  open,
  onClose,
  driverName,
  driverId,
  warehouseId,
  mode = 'earning',
  month,onSuccess
  
}) => {
  const [allTypes, setAllTypes] = useState<DropdownOption[]>([]);
  const [filteredTypes, setFilteredTypes] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [amount, setAmount] = useState('');

  useEffect(() => {
  if (!open) return;

  setLoading(true);
  getEarningsDeductionType(
    (res: any) => {
      if (res?.status === 200) {
        const types = res.data.map((i: any) => ({
          id: String(i.id),
          label: i.name,
          type: i.type,
        }));
        setAllTypes(types);
      }
      setLoading(false);
    },
    () => {
      toast.error('Failed to load transaction types');
      setLoading(false);
    }
  );
}, [open]);

useEffect(() => {
  if (allTypes.length === 0) {
    setFilteredTypes([]);
    return;
  }

  const excludedNames = [
    'amount earned per km per trip',
    'fixed amount earned per day',
    'amount earned per delivered order',
  ];

  const filtered = allTypes.filter(type => {
    const typeCategory = type.type?.toLowerCase();
    const typeName = type.label?.toLowerCase();
    if (excludedNames.includes(typeName)) {
      return false;
    }

    if (mode === 'earning') {
      return (
        typeCategory === 'earnings' ||
        typeCategory?.includes('earning')
      );
    } else {
      return (
        typeCategory === 'deductions' ||
        typeCategory === 'deduction' ||
        typeCategory?.includes('deduct')
      );
    }
  });

  setFilteredTypes(filtered);
  setSelectedType('');
}, [allTypes, mode]);



  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const validateForm = (): boolean => {
    if (!selectedType) {
      toast.error('Please select a transaction type');
      return false;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return false;
    }
    
    if (!driverId) {
      toast.error('Driver information is missing');
      return false;
    }
    
    return true;
  };

 const handleSubmit = () => {
    if (!validateForm()) return;
    
    addTransaction(
      driverId,
      warehouseId,
      selectedType,
      '1',
      parseFloat(amount),
      '',
      month,
      (response: any) => {
        if (response?.status === 200) {
          toast.success(response.message);
          resetForm();
          if (onSuccess) {
            onSuccess();
          }
          
          onClose();
        } else {
          toast.error(response?.message || `Failed to add ${mode.toLowerCase()}`);
        }
      },
      (error: any) => {
        toast.error(error?.message || `Failed to add ${mode.toLowerCase()}`);
      }
    );
  };
  const resetForm = () => {
    setSelectedType('');
    setAmount('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getModeColor = () => {
    return mode === 'earning' ? 'success' : 'error';
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" >
      <Box
        sx={{
          marginLeft: {
            xs: '0px',
            sm: '-180px',
          },
        }}
      >
      <Heading
        title={`Add ${mode === 'earning' ? 'Earnings' : 'Deductions'} - ${driverName || 'Driver'}`}
        closeIcon="true"
        onClose={handleClose}
        />
      </Box>
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>

      <Dropdown
        options={filteredTypes}
        selectedValue={selectedType}
        handleChange={e => setSelectedType(e.target.value)}
        label={`${mode === 'earning' ? 'Earnings' : 'Deductions'} Type`}
        placeholder={`Select ${mode === 'earning' ? 'earnings' : 'deductions'} type`}
        width="250px"
        disabled={loading || filteredTypes.length === 0}
        required
        />

        {filteredTypes.length === 0 && !loading && (
          <Typography color="text.secondary" variant="caption">
            No {mode.toLowerCase()} types available
          </Typography>
        )}

        <TextField
          label="Amount (₹)"
          value={amount}
          onChange={handleAmountChange}
          type="number"
          sx={{width:'250px'}}
          InputProps={{
            startAdornment: (
              <Typography sx={{ mr: 1, color: 'text.secondary' }}>₹</Typography>
            ),
            inputProps: { 
              min: 0, 
              step: 0.01,
              style: { 
                fontSize: '1.1rem',
                fontWeight: 500, 
                
              }
            }
          }}
          required
          error={amount !== '' && parseFloat(amount) <= 0}
          helperText={amount !== '' && parseFloat(amount) <= 0 ? 'Amount must be greater than 0' : ''}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          color={getModeColor()}
          disabled={!selectedType || !amount || parseFloat(amount) <= 0 || loading}
        >
          {loading ? 'Processing...' : `₹ Add ${mode === 'earning' ? 'Earning' : 'Deduction'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEarningModal;